import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { API } from "@/lib/api";
import { COACH_PROMPTS } from "@/data/parentingData";
import { useChildren } from "@/context/ChildrenContext";
import { useAuth } from "@/context/AuthContext";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Send, Sparkles, Loader2 } from "lucide-react";
import CoachMarkdown from "@/components/CoachMarkdown";
import Paywall from "@/components/Paywall";
import Mascot from "@/components/Mascot";

export default function CoachPage() {
  const { user } = useAuth();
  const { activeChild } = useChildren();
  const [params] = useSearchParams();
  const [message, setMessage] = useState(params.get("q") || "");
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);
  const [streamingText, setStreamingText] = useState("");
  const [streamingQuestion, setStreamingQuestion] = useState("");
  const [locked, setLocked] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/coach/history");
      setHistory(data);
      setLocked(false);
    } catch (err) {
      if (err?.response?.status === 402) {
        setLocked(true);
      }
    }
  };
  useEffect(() => { load(); }, []);

  const ask = async (text) => {
    const q = text || message;
    if (!q.trim() || busy) return;
    setBusy(true);
    setStreamingText("");
    setStreamingQuestion(q);

    const token = localStorage.getItem("parentia_token");
    try {
      const res = await fetch(`${API}/coach/stream`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          message: q,
          child_age: activeChild?.age,
          context: activeChild ? `Enfant : ${activeChild.name}, ${activeChild.age} ans` : null,
        }),
      });

      if (!res.ok || !res.body) {
        if (res.status === 402) {
          setLocked(true);
          throw new Error("Fonctionnalité réservée aux abonnés");
        }
        throw new Error("Erreur du coach");
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let full = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";
        for (const p of parts) {
          const line = p.trim();
          if (!line.startsWith("data:")) continue;
          const payload = line.slice(5).trim();
          let evt;
          try {
            evt = JSON.parse(payload);
          } catch {
            continue;
          }
          if (evt.error) {
            throw new Error(evt.error);
          }
          if (evt.delta) {
            full += evt.delta;
            setStreamingText((s) => s + evt.delta);
          }
        }
      }

      setMessage("");
      setStreamingText("");
      setStreamingQuestion("");
      toast.success("Réponse du coach 💛");
      await load();
    } catch (e) {
      toast.error(e?.message || "Erreur du coach");
      setStreamingText("");
      setStreamingQuestion("");
    }
    setBusy(false);
  };

  return (
    <div className="space-y-6" data-testid="ai-coach-chat-section">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-2xl bg-[#E3EAF7] grid place-items-center">
          <Sparkles className="h-5 w-5 text-[#23375E]" />
        </div>
        <div className="flex-1">
          <h1 className="text-3xl font-bold text-[#16233D]" style={{ fontFamily: "Outfit, sans-serif" }}>Coach IA parental</h1>
          <p className="text-[#77849C] text-sm">Réponses personnalisées et bienveillantes — propulsé par Claude Sonnet 5</p>
        </div>
        <Mascot pose="idea" size={96} anim="float" className="hidden sm:block shrink-0 mascot-pop" />
      </div>

      {locked && (
        <Paywall
          title="Le Coach IA fait partie des formules premium"
          message="Débloque Kynoria à vie pour 47 € pour poser toutes tes questions au coach."
        />
      )}

      {!locked && (
      <>
      <div className="soft-card p-6">
        <p className="text-xs font-bold uppercase tracking-wider text-[#77849C]">Questions rapides</p>
        <div className="mt-3 flex flex-wrap gap-2">
          {COACH_PROMPTS.map((p) => (
            <button key={p} data-testid={`quick-${p.substring(0,12)}`} onClick={() => ask(p)}
              disabled={busy}
              className="rounded-full bg-[#E3EAF7] hover:bg-[#D1DDF2] text-[#16233D] px-4 py-2 text-sm transition disabled:opacity-50">
              {p}
            </button>
          ))}
        </div>

        <div className="mt-6 space-y-3">
          <Textarea data-testid="coach-input" value={message} onChange={(e) => setMessage(e.target.value)}
            placeholder="Ex : Mon enfant de 4 ans refuse d'aller au lit, que faire ?"
            rows={3} />
          <div className="flex justify-end">
            <Button data-testid="coach-send-btn" onClick={() => ask()} disabled={busy || !message.trim()} className="brand-btn rounded-full px-5">
              {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> En réflexion...</> : <><Send className="h-4 w-4 mr-2" /> Envoyer</>}
            </Button>
          </div>
        </div>
      </div>

      {streamingQuestion && (
        <article className="soft-card p-6" data-testid="streaming-conv">
          <div className="text-xs text-[#77849C]">En direct...</div>
          <div className="mt-3 rounded-2xl bg-[#EDF2F9] p-4">
            <p className="text-xs font-bold uppercase tracking-wider text-[#77849C] mb-1">Ta question</p>
            <p className="text-[#16233D]">{streamingQuestion}</p>
          </div>
          <div className="mt-3 rounded-2xl bg-[#E3EAF7] p-4">
            <p className="text-xs font-bold uppercase tracking-wider text-[#16233D] mb-1 flex items-center gap-2">
              Réponse du coach
              <Loader2 className="h-3 w-3 animate-spin" />
            </p>
            <div data-testid="streaming-text" className="text-[#16233D] whitespace-pre-wrap leading-relaxed">
              <CoachMarkdown text={streamingText} />
              <span className="inline-block w-2 h-4 bg-[#23375E] ml-1 animate-pulse align-middle" />
            </div>
          </div>
        </article>
      )}

      <div className="space-y-4">
        {history.map((h) => (
          <article key={h.id} data-testid={`conv-${h.id}`} className="soft-card p-6">
            <div className="text-xs text-[#77849C]">{new Date(h.created_at).toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" })}</div>
            <div className="mt-3 rounded-2xl bg-[#EDF2F9] p-4">
              <p className="text-xs font-bold uppercase tracking-wider text-[#77849C] mb-1">Ta question</p>
              <p className="text-[#16233D]">{h.question}</p>
            </div>
            <div className="mt-3 rounded-2xl bg-[#E3EAF7] p-4">
              <p className="text-xs font-bold uppercase tracking-wider text-[#16233D] mb-1">Réponse du coach</p>
              <CoachMarkdown text={h.answer} />
            </div>
          </article>
        ))}
      </div>
      </>
      )}
    </div>
  );
}
